import { useEffect, useRef, useState } from 'react';
import FocusLock from 'react-focus-lock';
import cx from 'classnames';
import { getIsReducedMotion } from '@dx-ui/utilities-accessibility';

export type DrawerPanelProps = {
  isOpen: boolean;
  children: React.ReactNode;
  alignment: 'left' | 'right';
  /**
   *  Id of the body of your entire app, used to select for all dom elements within app without affecting elements that sit outside the app
   */
  appWrapperId: string;
  /**
   *  Id of the wrapper element you want to keep interactive while the panel is open. Example: Navigation header while nav menu is open
   */
  interactiveElementWrapperId: string;
  panelProps?: React.ComponentProps<'div'>;
  /**
   * Allows you to choose a specific point to return focus to or allow for default returnFocus
   */
  returnFocus?: () => boolean;
  className?: string;
};

const useDrawerPageLock = (
  isOpen: boolean,
  appWrapperId: string,
  interactiveElementWrapperId: string
) => {
  const nonInteractiveElements = useRef<NodeListOf<Element> | undefined>(undefined);
  const previousOverflowStyles = useRef<{
    body: string;
    documentElement: string;
  } | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const restoreAccessibilityTree = () => {
      nonInteractiveElements.current?.forEach((nonInteractiveElement) => {
        nonInteractiveElement.removeAttribute('aria-hidden');
      });
      nonInteractiveElements.current = undefined;
    };

    const unlockScroll = () => {
      if (!previousOverflowStyles.current) {
        return;
      }

      document.body.style.overflow = previousOverflowStyles.current.body ?? '';
      document.documentElement.style.overflow =
        previousOverflowStyles.current.documentElement ?? '';
      previousOverflowStyles.current = null;
    };

    previousOverflowStyles.current = {
      body: document.body.style.overflow,
      documentElement: document.documentElement.style.overflow,
    };

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    const nonInteractiveElems = document?.querySelectorAll(
      // children of element with interactiveElementWrapperId will not have aria-hidden="true" applied
      `#${appWrapperId} *:not(#${interactiveElementWrapperId}, #${interactiveElementWrapperId} *):not([aria-hidden="true"])`
    );
    nonInteractiveElems.forEach((nonInteractiveElement) => {
      if (nonInteractiveElement) {
        nonInteractiveElement.setAttribute('aria-hidden', 'true');
      }
    });
    /** interactiveElementWrapper should always be available in the accessibility tree,
     *  the selector above will also set aria-hidden on any parents of the interactiveElementWrapper in between it and element with appWrapperId
     * This function removes aria hidden from the drawer's parents */
    let drawerWrapper: Element | null = document?.querySelector(`#${interactiveElementWrapperId}`);
    const els: Element[] = [];
    while (drawerWrapper) {
      els.push(drawerWrapper);
      drawerWrapper = drawerWrapper.parentElement;
    }
    els?.forEach((navParentElement) => {
      if (navParentElement) {
        navParentElement.removeAttribute('aria-hidden');
      }
    });
    nonInteractiveElements.current = nonInteractiveElems;
    return () => {
      unlockScroll();
      restoreAccessibilityTree();
    };
  }, [isOpen, appWrapperId, interactiveElementWrapperId]);
};

const getDrawerWindowClassName = (
  alignment: 'left' | 'right',
  isOpen: boolean,
  className?: string
) =>
  cx(
    'absolute top-0 z-50 h-screen w-80 overflow-y-scroll border-t border-surface bg-surface transition-all',
    {
      'ltr:motion-safe:animate-slideInLeft rtl:motion-safe:animate-slideInRight':
        isOpen && alignment === 'left',
      'ltr:motion-safe:animate-slideInRight rtl:motion-safe:animate-slideInLeft':
        isOpen && alignment === 'right',
      'end-0': alignment === 'right',
      'hidden -translate-x-full': !isOpen,
    },
    className
  );

type DrawerWindowProps = {
  isOpen: boolean;
  children: React.ReactNode;
  className?: string;
  panelProps?: React.ComponentProps<'div'>;
};

/**
 * Right-aligned content is withheld until slide-in finishes so FocusLock does not
 * jump focus while the panel is still off-screen. This component remounts when
 * `isOpen` changes (via `key` in the parent) so that delay resets naturally.
 */
const RightDrawerWindow = ({ isOpen, children, className, panelProps }: DrawerWindowProps) => {
  const [hasSlideInCompleted, setHasSlideInCompleted] = useState(false);
  const shouldShowContent = isOpen && (hasSlideInCompleted || getIsReducedMotion());

  return (
    <div
      className={getDrawerWindowClassName('right', isOpen, className)}
      onAnimationEnd={() => setHasSlideInCompleted(true)}
      hidden={!isOpen}
      data-testid="panelWindow-right"
      {...panelProps}
    >
      {shouldShowContent ? <div className="h-full animate-fadein">{children}</div> : null}
    </div>
  );
};

export const DrawerPanel = ({
  isOpen,
  children,
  className,
  alignment,
  appWrapperId,
  interactiveElementWrapperId,
  panelProps,
  returnFocus,
}: DrawerPanelProps) => {
  useDrawerPageLock(isOpen, appWrapperId, interactiveElementWrapperId);

  return (
    <FocusLock disabled={!isOpen} returnFocus={returnFocus ? returnFocus : true}>
      {alignment === 'right' ? (
        <RightDrawerWindow
          key={isOpen ? 'open' : 'closed'}
          isOpen={isOpen}
          className={className}
          panelProps={panelProps}
        >
          {children}
        </RightDrawerWindow>
      ) : (
        <div
          className={getDrawerWindowClassName('left', isOpen, className)}
          hidden={!isOpen}
          data-testid="panelWindow-left"
          {...panelProps}
        >
          {children}
        </div>
      )}
    </FocusLock>
  );
};
